import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';


import ChemicalCalculator from './components/ChemicalCalculator';
import BeneficialCalculator from './components/BeneficialCalculator';
import IPMPlanGenerator from './components/IPMPlanGenerator';
import TankMixingCalculator from './components/TankMixingCalculator';

const PremiumCalculatorSuite = () => {
  const { user } = useAuth();
  const { getSubscriptionStatus, isPremiumUser, isTrialUser } = useSubscription();
  const navigate = useNavigate();
  const [activeCalculator, setActiveCalculator] = useState('chemical');
  const [calculationHistory, setCalculationHistory] = useState([]);

  const calculators = [
    {
      id: 'chemical',
      name: 'Chemical Calculator',
      icon: 'Calculator',
      description: 'Precise mixing ratios and application rates',
      component: ChemicalCalculator,
      premium: false
    },
    {
      id: 'beneficial',
      name: 'Beneficial Calculator', 
      icon: 'Bug',
      description: 'Optimal release rates for biological controls',
      component: BeneficialCalculator,
      premium: true
    },
    {
      id: 'tank-mixing',
      name: 'Tank Mixing Procedures',
      icon: 'FlaskConical',
      description: 'Professional tank mixing with compatibility checks',
      component: TankMixingCalculator,
      premium: true
    },
    {
      id: 'ipm-plan',
      name: 'IPM Plan Generator',
      icon: 'FileText', 
      description: 'AI-generated treatment rotation plans',
      component: IPMPlanGenerator,
      premium: true
    }
  ];

  const ActiveComponent = calculators?.find(calc => calc?.id === activeCalculator)?.component;
  const activeCalc = calculators?.find(calc => calc?.id === activeCalculator);

  const handleCalculationComplete = (calculation) => {
    setCalculationHistory(prev => [{
      ...calculation,
      timestamp: new Date()?.toISOString(),
      calculator: activeCalculator
    }, ...prev]?.slice(0, 10));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Premium Calculator Suite</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Advanced IPM calculation tools</p>
        </div>

        {/* Calculator Tabs */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max sm:min-w-0">
            {calculators?.map((calc) => (
              <button
                key={calc?.id}
                onClick={() => setActiveCalculator(calc?.id)}
                className={`
                  flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all
                  ${activeCalculator === calc?.id
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }
                `}
              >
                <Icon name={calc?.icon} size={18} />
                <span className="text-sm">{calc?.name}</span>
                {calc?.premium && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    Premium
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Calculator Description */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">{activeCalc?.name}</h3>
              <p className="text-sm text-blue-700">{activeCalc?.description}</p>
            </div>
          </div>
        </div>

        {/* Calculator Content */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 lg:p-8">
          {ActiveComponent && (
            <ActiveComponent 
              onCalculationComplete={handleCalculationComplete}
              isPreviewMode={false}
            />
          )}
        </div>

        {/* Calculation History */}
        {calculationHistory?.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
              <Icon name="History" size={20} />
              <span>Recent Calculations</span>
            </h3>
            <div className="space-y-3">
              {calculationHistory?.slice(0, 5)?.map((calc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {calculators?.find(c => c?.id === calc?.calculator)?.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(calc?.timestamp)?.toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    {calc?.chemicalAmount && (
                      <div className="text-sm font-semibold text-green-600">
                        {calc?.chemicalAmount} oz
                      </div>
                    )}
                    {calc?.totalQuantity && (
                      <div className="text-sm font-semibold text-green-600">
                        {calc?.totalQuantity} beneficials
                      </div>
                    )}
                    {calc?.estimatedCost && (
                      <div className="text-xs text-gray-500">
                        ${calc?.estimatedCost}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumCalculatorSuite;